import { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { stories } from "../content/siteContent";
import { Reveal, Overline } from "./Reveal";
import { IconArrow } from "./Icons";

const StoryVideo = ({ story, index, lang }) => {
    const [playing, setPlaying] = useState(false);
    return (
        <div className="relative">
            <video
                src={story.src}
                poster={story.poster}
                controls
                playsInline
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                className="aspect-[9/16] w-full bg-pine-900 object-cover"
                data-testid={`story-video-${index + 1}`}
            />
            {story.thumb && (
                <div
                    aria-hidden={playing}
                    className={`pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-pine-900/80 via-pine-900/35 to-transparent p-4 pt-5 transition-opacity duration-500 ${
                        playing ? "opacity-0" : "opacity-100"
                    }`}
                    data-testid={`story-thumb-${index + 1}`}
                >
                    <p className="font-display text-lg font-semibold leading-snug text-cream">{story.thumb[lang]}</p>
                </div>
            )}
        </div>
    );
};

const StoriesSection = () => {
    const { t, lang } = useLanguage();
    if (stories.length === 0) return null;
    return (
        <section id="stories" className="scroll-mt-24 bg-tint/60 py-12 md:py-20" aria-labelledby="stories-title" data-testid="stories-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <Overline>{t.stories.overline}</Overline>
                    <h2 id="stories-title" className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        {t.stories.title}
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-mist md:text-lg">{t.stories.subtitle}</p>
                </Reveal>
                <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {stories.map((story, i) => (
                        <Reveal key={story.src} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                            <figure
                                className="flex h-full flex-col overflow-hidden rounded-2xl border border-ink/10 bg-surface shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_45px_-25px_rgba(0,77,77,0.4)]"
                                data-testid={`story-card-${i + 1}`}
                            >
                                <StoryVideo story={story} index={i} lang={lang} />
                                <figcaption className="flex flex-1 flex-col p-5">
                                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gold-600">{story.category[lang]}</span>
                                    <h3 className="mt-2 text-base font-bold leading-snug text-ink">{story.title[lang]}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-mist">{story.summary[lang]}</p>
                                    <details className="group mt-3" data-testid={`story-details-${i + 1}`}>
                                        <summary className="inline-flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-xs font-bold text-pine transition-colors duration-300 hover:text-gold-600 [&::-webkit-details-marker]:hidden">
                                            {t.stories.details}
                                            <IconArrow className="h-3.5 w-3.5 -rotate-90 transition-transform duration-300 group-open:rotate-0 rtl:-rotate-90 rtl:group-open:rotate-0" />
                                        </summary>
                                        <div className="space-y-3 border-s-2 border-gold-500/50 ps-3 pt-1 text-sm leading-relaxed text-mist">
                                            <p>
                                                <strong className="text-ink">{t.stories.challenge}: </strong>
                                                {story.challenge[lang]}
                                            </p>
                                            <p>
                                                <strong className="text-ink">{t.stories.approach}: </strong>
                                                {story.approach[lang]}
                                            </p>
                                            <p>
                                                <strong className="text-ink">{t.stories.outcome}: </strong>
                                                {story.outcome[lang]}
                                            </p>
                                            <p className="font-semibold text-pine">{story.quote[lang]}</p>
                                        </div>
                                    </details>
                                    <a
                                        href="#appointment"
                                        data-testid={`story-cta-${i + 1}`}
                                        className="mt-auto inline-flex min-h-[44px] items-center gap-2 pt-3 text-sm font-bold text-pine transition-colors duration-300 hover:text-gold-600"
                                    >
                                        {story.cta[lang]}
                                        <IconArrow className="h-4 w-4" />
                                    </a>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StoriesSection;
